// Global unread counts provider for nav badges across all pages
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuthCtx } from './AuthContext';
import { listenMyThreads, countUnreadFromThreadDocs, getUnreadThreadsCount } from '../lib/inboxRepo';
import type { UserThreadDoc } from '../lib/inboxRepo';
import { listenMyNotifications, getUnreadNotificationsCountNew } from '../lib/firestore/notificationsNew';
import {
  listenOrgApplications,
  getUnreadApplicationsCount,
  listenEmtApplications,
  getUnreadApplicationsCountForEmt,
} from '../lib/firestore/applications';
import { isNotificationRead } from '../lib/notifications/presentation';
import { playNotificationSound, unlockNotificationAudio } from '../lib/notifications/playNotificationSound';
import { readEmtNotificationPrefs } from '../lib/preferences';
import type { EMTProfile } from '../types/firestore';
export interface UnreadCounts {
  messages: number;
  notifications: number;
  applications: number;
}

export interface UnreadCountsContextValue extends UnreadCounts {
  /** True after first server-backed snapshot for badges (avoids flashing placeholder counts). */
  hydrated: boolean;
  /**
   * True after the first reliable message unread value (server one-shot and/or first non-cache listener).
   */
  messagesBadgeLive: boolean;
  notificationsBadgeLive: boolean;
  /** Re-query server counts for messages + notifications (call after mark-read / mark-all). */
  refreshBadgeCounts: () => Promise<void>;
  /**
   * When the EMT/employer inbox is viewing a thread, exclude it from the nav badge until Firestore catches up.
   */
  setMessagesThreadInView: (threadId: string | null) => void;
}

const defaultValue: UnreadCountsContextValue = {
  messages: 0,
  notifications: 0,
  applications: 0,
  hydrated: false,
  messagesBadgeLive: false,
  notificationsBadgeLive: false,
  refreshBadgeCounts: async () => {},
  setMessagesThreadInView: () => {},
};

const UnreadCountsContext = createContext<UnreadCountsContextValue>(defaultValue);

/** Ignore realtime notification snapshots briefly after a server refresh to avoid stale cache overwriting the badge. */
const LISTENER_SUPPRESS_MS = 750;

export function UnreadCountsProvider({ children }: { children: React.ReactNode }) {
  const { user, profile } = useAuthCtx();
  const [counts, setCounts] = useState<UnreadCounts>({ messages: 0, notifications: 0, applications: 0 });
  const [hydrated, setHydrated] = useState(false);
  const [messagesBadgeLive, setMessagesBadgeLive] = useState(false);
  const [notificationsBadgeLive, setNotificationsBadgeLive] = useState(false);
  const suppressNotifListenerUntil = useRef(0);
  const suppressMsgListenerUntil = useRef(0);
  const openMessagesThreadIdRef = useRef<string | null>(null);
  const knownNotifIdsRef = useRef<Set<string> | null>(null);
  const soundReadyRef = useRef(false);

  // Unlock Web Audio after a user gesture so notification beeps can play later.
  useEffect(() => {
    if (!user) return;
    const unlock = () => {
      unlockNotificationAudio();
      soundReadyRef.current = true;
    };
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [user]);

  const safeSetCounts = useCallback((updater: (prev: UnreadCounts) => UnreadCounts) => {
    setCounts((prev) => {
      try {
        return updater(prev);
      } catch {
        return prev;
      }
    });
  }, []);

  const setMessagesThreadInView = useCallback(
    (threadId: string | null) => {
      const t = threadId?.trim() || null;
      openMessagesThreadIdRef.current = t;
      if (!user) return;
      void getUnreadThreadsCount(user.uid, { excludeThreadId: t })
        .then((count) => {
          setMessagesBadgeLive(true);
          safeSetCounts((prev) => ({ ...prev, messages: count }));
        })
        .catch(() => {});
    },
    [user, safeSetCounts]
  );

  /** Re-fetch unread from server (notifications + message threads) while briefly suppressing listeners. */
  const refreshBadgeCounts = useCallback(async () => {
    if (!user) return;
    const now = Date.now();
    suppressNotifListenerUntil.current = now + LISTENER_SUPPRESS_MS;
    suppressMsgListenerUntil.current = now + LISTENER_SUPPRESS_MS;
    try {
      const [notificationsCount, messagesCount] = await Promise.all([
        getUnreadNotificationsCountNew(user.uid).catch(() => 0),
        getUnreadThreadsCount(user.uid, { excludeThreadId: openMessagesThreadIdRef.current }).catch(() => 0),
      ]);
      safeSetCounts((prev) => ({
        ...prev,
        notifications: notificationsCount,
        messages: messagesCount,
      }));
      setMessagesBadgeLive(true);
      setNotificationsBadgeLive(true);
    } catch {
      /* keep previous */
    }
  }, [user, safeSetCounts]);

  useEffect(() => {
    if (!user) {
      setCounts({ messages: 0, notifications: 0, applications: 0 });
      setHydrated(false);
      setMessagesBadgeLive(false);
      setNotificationsBadgeLive(false);
      openMessagesThreadIdRef.current = null;
      knownNotifIdsRef.current = null;
      return;
    }

    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('regInProgress')) {
      setCounts({ messages: 0, notifications: 0, applications: 0 });
      setHydrated(true);
      setMessagesBadgeLive(true);
      setNotificationsBadgeLive(true);
      return;
    }

    let unsubscribeThreads: () => void = () => {};
    let unsubscribeNotifications: () => void = () => {};
    let unsubscribeApplications: (() => void) | null = null;
    let cancelled = false;

    (async () => {
      let applicationsCount = 0;
      let serverMessagesOk = false;
      let serverMessagesUnread = 0;
      try {
        if (profile?.role === 'employer') {
          applicationsCount = await getUnreadApplicationsCount(user.uid).catch(() => 0);
        } else if (profile?.role === 'emt') {
          applicationsCount = await getUnreadApplicationsCountForEmt(user.uid).catch(() => 0);
        }
        if (cancelled) return;

        try {
          serverMessagesUnread = await getUnreadThreadsCount(user.uid, {
            excludeThreadId: openMessagesThreadIdRef.current,
          });
          serverMessagesOk = true;
        } catch {
          serverMessagesOk = false;
        }

        if (cancelled) return;
        setCounts({
          messages: serverMessagesOk ? serverMessagesUnread : 0,
          notifications: 0,
          applications: applicationsCount,
        });
        setHydrated(true);
        if (serverMessagesOk) {
          setMessagesBadgeLive(true);
        }
      } catch {
        if (!cancelled) {
          setCounts({ messages: 0, notifications: 0, applications: 0 });
          setHydrated(true);
          setMessagesBadgeLive(true);
          setNotificationsBadgeLive(true);
        }
      }

      if (cancelled) return;

      try {
        unsubscribeThreads = listenMyThreads(user.uid, (threads: UserThreadDoc[], meta) => {
          if (Date.now() < suppressMsgListenerUntil.current) return;
          if (meta.fromCache) return;
          setMessagesBadgeLive(true);
          const unreadCount = countUnreadFromThreadDocs(threads, {
            excludeThreadId: openMessagesThreadIdRef.current,
          });
          safeSetCounts((prev) => ({ ...prev, messages: unreadCount }));
        });

        unsubscribeNotifications = listenMyNotifications(
          user.uid,
          (notifications, meta) => {
            if (Date.now() < suppressNotifListenerUntil.current) return;
            if (meta.fromCache) return;
            setNotificationsBadgeLive(true);
            const unreadCount = notifications.filter((n) => !isNotificationRead(n)).length;
            safeSetCounts((prev) => ({ ...prev, notifications: unreadCount }));

            const ids = new Set(notifications.map((n) => n.id));
            const prev = knownNotifIdsRef.current;
            if (prev === null) {
              knownNotifIdsRef.current = ids;
            } else {
              let hasNewUnread = false;
              for (const n of notifications) {
                if (!prev.has(n.id) && !isNotificationRead(n)) {
                  hasNewUnread = true;
                  break;
                }
              }
              knownNotifIdsRef.current = ids;
              if (hasNewUnread && profile?.role === 'emt') {
                const soundOn = readEmtNotificationPrefs(profile as EMTProfile).inAppSound;
                if (soundOn) playNotificationSound();
              }
            }
          },
          { maxItems: 100 }
        );

        if (profile?.role === 'employer') {
          // Every application on the org's jobs (teammates included), not only the ones this user
          // posted: the badge has to agree with the inbox (QA 2026-10-06, listenOrgApplications).
          unsubscribeApplications = listenOrgApplications(
            (profile as { orgId?: string | null }).orgId ?? null,
            user.uid,
            (apps) => {
              const unreadCount = apps.filter((a) => (a as { isUnreadForEmployer?: boolean }).isUnreadForEmployer === true).length;
              safeSetCounts((prev) => ({ ...prev, applications: unreadCount }));
            },
            () => safeSetCounts((prev) => ({ ...prev, applications: 0 }))
          );
        } else if (profile?.role === 'emt') {
          unsubscribeApplications = listenEmtApplications(user.uid, async () => {
            try {
              const unreadCount = await getUnreadApplicationsCountForEmt(user.uid);
              safeSetCounts((prev) => ({ ...prev, applications: unreadCount }));
            } catch {
              safeSetCounts((prev) => ({ ...prev, applications: 0 }));
            }
          });
        }
      } catch {
        if (!cancelled) {
          setCounts({ messages: 0, notifications: 0, applications: 0 });
          setHydrated(true);
          setMessagesBadgeLive(true);
          setNotificationsBadgeLive(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribeThreads();
      unsubscribeNotifications();
      if (unsubscribeApplications) unsubscribeApplications();
    };
  }, [user, profile, safeSetCounts]);

  const value: UnreadCountsContextValue = {
    ...counts,
    hydrated,
    messagesBadgeLive,
    notificationsBadgeLive,
    refreshBadgeCounts,
    setMessagesThreadInView,
  };

  return <UnreadCountsContext.Provider value={value}>{children}</UnreadCountsContext.Provider>;
}

export function useUnreadCounts(): UnreadCountsContextValue {
  return useContext(UnreadCountsContext);
}
